import { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { useAppState } from "../context/AppStateContext";
import { useToast } from "../context/ToastContext";
import {
  fetchIssuePoRegister,
  fetchPoReceiptBalance,
  fetchOpenPoIdsForInward,
  postStoreInward,
  fetchStoreInwardRegister,
  type IssuePoHeader,
  type PoReceiptBalanceLine,
  type StoreInwardReceipt,
} from "../api/mrrInventoryApi";
import { SearchableSelect } from "../components/SearchableSelect";
import { parseQuantityInput, sanitizeQtyFieldInput } from "../utils/qtyInput";
import { resolveStoreYardForApplication } from "../utils/storeYards";

type ViewMode = "inward" | "register";

interface AggregatedPoItem {
  catalogStandardItemId: string;
  resourceItemLabel: string;
  resourceItemUnit: string;
  poQty: number;
  priorReceivedQty: number;
  yetToReceive: number;
}

function roundQtyDisplay(n: number) {
  return Math.round(n * 1000) / 1000;
}

function lineKey(catalogStandardItemId: string, resourceItemLabel: string) {
  return `${catalogStandardItemId}\t${resourceItemLabel}`;
}

function buildAggregatedFromBalance(items: PoReceiptBalanceLine[]): AggregatedPoItem[] {
  return items
    .filter((it) => it.yetToReceive > 0)
    .map((it) => ({
      catalogStandardItemId: it.catalogStandardItemId,
      resourceItemLabel: it.resourceItemLabel,
      resourceItemUnit: it.resourceItemUnit,
      poQty: it.poQty,
      priorReceivedQty: it.receivedQty,
      yetToReceive: it.yetToReceive,
    }));
}

export function InventoryInwardPage() {
  const { storeYards, applications, geoZones } = useAppState();
  const { showToast } = useToast();
  const sortedYards = useMemo(() => [...storeYards].sort((a, b) => a.sortOrder - b.sortOrder), [storeYards]);

  const [viewMode, setViewMode] = useState<ViewMode>("inward");

  // Inward Tab State
  const [poList, setPoList] = useState<IssuePoHeader[]>([]);
  const [poLoading, setPoLoading] = useState(false);
  const [selectedPoId, setSelectedPoId] = useState("");
  const [poItemsLoading, setPoItemsLoading] = useState(false);
  const [aggregatedItems, setAggregatedItems] = useState<AggregatedPoItem[]>([]);
  
  const [storeYardId, setStoreYardId] = useState("");
  const [receivedBy, setReceivedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [lineQty, setLineQty] = useState<Record<string, string>>({});
  const [lineRejectQty, setLineRejectQty] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Register Tab State
  const [registerList, setRegisterList] = useState<StoreInwardReceipt[]>([]);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);

  // Load POs with open receipt balance only
  useEffect(() => {
    if (viewMode !== "inward") return;
    let cancelled = false;
    setPoLoading(true);
    Promise.all([fetchIssuePoRegister({ limit: 1000, sort: "desc" }), fetchOpenPoIdsForInward()])
      .then(([allPos, openPoIds]) => {
        if (cancelled) return;
        const openSet = new Set(openPoIds);
        setPoList(allPos.filter((p) => openSet.has(p.id)));
      })
      .catch((err) => {
        if (!cancelled) showToast(err instanceof Error ? err.message : "Failed to load POs", "error");
      })
      .finally(() => {
        if (!cancelled) setPoLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [viewMode, showToast]);

  // Load PO receipt balance when a PO is selected
  useEffect(() => {
    if (!selectedPoId) {
      setAggregatedItems([]);
      setLineQty({});
      setLineRejectQty({});
      return;
    }
    let cancelled = false;
    setPoItemsLoading(true);
    const po = poList.find((p) => p.id === selectedPoId);
    fetchPoReceiptBalance(selectedPoId)
      .then((balance) => {
        if (cancelled) return;
        if (!balance.hasOpenBalance) {
          showToast("This PO is fully received.", "error");
          setSelectedPoId("");
          return;
        }
        const agg = buildAggregatedFromBalance(balance.items);
        setAggregatedItems(agg);
        const qtyMap: Record<string, string> = {};
        const rejMap: Record<string, string> = {};
        for (const it of agg) {
          const key = lineKey(it.catalogStandardItemId, it.resourceItemLabel);
          qtyMap[key] = String(roundQtyDisplay(it.yetToReceive));
          rejMap[key] = "0";
        }
        setLineQty(qtyMap);
        setLineRejectQty(rejMap);
        if (sortedYards.length > 0) {
          const shipYard = po?.shipToStoreYardId?.trim();
          if (shipYard && sortedYards.some((y) => y.id === shipYard)) {
            setStoreYardId(shipYard);
          } else {
            const appId = po?.shipToApplicationId?.trim();
            const app = appId ? applications.find((a) => a.id === appId) : null;
            const resolved = app ? resolveStoreYardForApplication(app, sortedYards, geoZones) : null;
            setStoreYardId(resolved?.id ?? sortedYards[0].id);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) showToast(err instanceof Error ? err.message : "Failed to load PO balance", "error");
      })
      .finally(() => {
        if (!cancelled) setPoItemsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPoId, poList, showToast, sortedYards, applications, geoZones]);

  // Load Register
  const loadRegister = useCallback(async () => {
    setRegisterLoading(true);
    setRegisterError(null);
    try {
      const data = await fetchStoreInwardRegister({ limit: 1000, sort: "desc" });
      setRegisterList(data);
    } catch (e) {
      setRegisterError(e instanceof Error ? e.message : String(e));
      setRegisterList([]);
    } finally {
      setRegisterLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode === "register") {
      void loadRegister();
    }
  }, [viewMode, loadRegister]);

  const selectedPo = useMemo(() => poList.find((p) => p.id === selectedPoId), [poList, selectedPoId]);
  const poSelectOptions = useMemo(
    () => [
      {
        value: "",
        label: poLoading
          ? "Loading POs..."
          : poList.length === 0
            ? "No POs with pending quantity"
            : "Select PO…",
      },
      ...poList.map((p) => ({
        value: p.id,
        label: `${p.poNumber} — ${p.milestoneTitle} — ${p.subcontractorFirmName}`,
      })),
    ],
    [poList, poLoading]
  );

  const submitReceipt = async () => {
    if (!selectedPo) return;
    if (!storeYardId.trim()) {
      showToast("Select a store / yard.", "error");
      return;
    }

    const lines = aggregatedItems.map((it) => {
      const key = lineKey(it.catalogStandardItemId, it.resourceItemLabel);
      const qtyReceived = parseQuantityInput(lineQty[key]);
      const qtyRejected = parseQuantityInput(lineRejectQty[key]);
      return {
        catalogStandardItemId: it.catalogStandardItemId || undefined,
        resourceItemLabel: it.resourceItemLabel,
        resourceItemUnit: it.resourceItemUnit,
        qtyReceived,
        qtyRejected,
        yetToReceive: it.yetToReceive,
      };
    });

    if (lines.every((l) => l.qtyReceived <= 0 && l.qtyRejected <= 0)) {
      showToast("Enter at least one received or damaged quantity.", "error");
      return;
    }

    for (const l of lines) {
      if (l.qtyRejected > l.qtyReceived + 0.000001) {
        showToast(`Damaged quantity for "${l.resourceItemLabel}" cannot exceed received quantity.`, "error");
        return;
      }
      if (l.qtyReceived > 0 && l.qtyReceived - l.yetToReceive > 0.000001) {
        showToast(
          `Received quantity for "${l.resourceItemLabel}" exceeds yet to receive (${roundQtyDisplay(l.yetToReceive)}).`,
          "error",
        );
        return;
      }
    }

    setSubmitting(true);
    try {
      const saved = await postStoreInward({
        poId: selectedPo.id,
        poNumber: selectedPo.poNumber,
        storeYardId: storeYardId.trim(),
        receivedBy: receivedBy.trim() || undefined,
        notes: notes.trim() || undefined,
        lines: lines.map(({ yetToReceive: _y, ...l }) => l),
      });
      showToast(`Inward transaction saved: ${saved.id}`, "success");
      // Reset form
      setSelectedPoId("");
      setReceivedBy("");
      setNotes("");
      setViewMode("register");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Save failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="inventory-inward-page">
      <PageHeader
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Inventory", to: "/inventory/inward" },
          { label: "Inward" },
        ]}
        title="Inventory — Inward"
      />

      <div className="issue-mode-toggle-wrap">
        <div className="issue-mode-toggle" role="tablist" aria-label="Inward mode">
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "inward"}
            className={`issue-mode-toggle__btn ${viewMode === "inward" ? "is-active" : ""}`}
            onClick={() => setViewMode("inward")}
          >
            Inward
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "register"}
            className={`issue-mode-toggle__btn ${viewMode === "register" ? "is-active" : ""}`}
            onClick={() => setViewMode("register")}
          >
            Inward Register
          </button>
        </div>
      </div>

      {viewMode === "inward" ? (
        <>
          <div className="card card--gov card--elevated" style={{ marginBottom: "1.25rem" }}>
            <h3 className="form-section-title" style={{ marginTop: 0 }}>
              Search Purchase Order
            </h3>
            <div className="searchable-select-field">
              <SearchableSelect
                id="po-search"
                options={poSelectOptions}
                value={selectedPoId}
                onChange={setSelectedPoId}
                placeholder="Search by PO Number or Subcontractor…"
              />
            </div>
          </div>

          {selectedPoId && (
            <div className="card card--gov card--elevated">
              <h3 className="form-section-title" style={{ marginTop: 0 }}>
                Inward Form
              </h3>
              {poItemsLoading ? (
                <p className="text-muted">Loading items…</p>
              ) : aggregatedItems.length === 0 ? (
                <p className="text-muted">This PO has no valid items to inward.</p>
              ) : (
                <>
                  <div className="goods-receipts-filters__grid" style={{ marginBottom: "1.5rem" }}>
                    <label className="goods-receipts-filters__field">
                      <span className="goods-receipts-filters__label">Store / yard</span>
                      <select
                        className="form-control"
                        value={storeYardId}
                        onChange={(e) => setStoreYardId(e.target.value)}
                        disabled={sortedYards.length === 0}
                      >
                        {sortedYards.length === 0 ? (
                          <option value="">No store yards — configure Stores / yards</option>
                        ) : (
                          sortedYards.map((y) => (
                            <option key={y.id} value={y.id}>
                              {y.name}
                            </option>
                          ))
                        )}
                      </select>
                    </label>

                    <label className="goods-receipts-filters__field">
                      <span className="goods-receipts-filters__label">Received by (optional)</span>
                      <input
                        className="form-control"
                        value={receivedBy}
                        onChange={(e) => setReceivedBy(e.target.value)}
                        placeholder="Name or user ID"
                      />
                    </label>
                  </div>

                  <h4 className="form-section-title inv-receive-modal__table-title">Item Quantities</h4>
                  <div className="inv-receive-modal__table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Unit</th>
                          <th>PO Qty</th>
                          <th style={{ width: "8rem" }}>Received Qty</th>
                          <th style={{ width: "8rem" }}>Damaged Qty</th>
                          <th style={{ width: "8rem" }}>Yet to Received</th>
                        </tr>
                      </thead>
                      <tbody>
                        {aggregatedItems.map((it) => {
                          const key = lineKey(it.catalogStandardItemId, it.resourceItemLabel);
                          const enteredReceived = parseQuantityInput(lineQty[key] ?? "");
                          const remainingYet = Math.max(0, roundQtyDisplay(it.yetToReceive - enteredReceived));
                          return (
                            <tr key={key}>
                              <td>{it.resourceItemLabel}</td>
                              <td>{it.resourceItemUnit}</td>
                              <td>{roundQtyDisplay(it.poQty)}</td>
                              <td>
                                <input
                                  className="inv-receive-modal__input inv-receive-modal__qty-input"
                                  type="text"
                                  inputMode="decimal"
                                  autoComplete="off"
                                  value={lineQty[key] ?? ""}
                                  onChange={(e) =>
                                    setLineQty((prev) => ({
                                      ...prev,
                                      [key]: sanitizeQtyFieldInput(e.target.value),
                                    }))
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="inv-receive-modal__input inv-receive-modal__qty-input"
                                  type="text"
                                  inputMode="decimal"
                                  autoComplete="off"
                                  value={lineRejectQty[key] ?? ""}
                                  onChange={(e) =>
                                    setLineRejectQty((prev) => ({
                                      ...prev,
                                      [key]: sanitizeQtyFieldInput(e.target.value),
                                    }))
                                  }
                                />
                              </td>
                              <td>{remainingYet}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="inv-receive-modal__actions" style={{ marginTop: "1.5rem" }}>
                    <button
                      type="button"
                      className="btn btn--primary btn--tg"
                      disabled={submitting || sortedYards.length === 0}
                      onClick={() => void submitReceipt()}
                    >
                      {submitting ? "Recording…" : "Record Inward"}
                    </button>
                    <button
                      type="button"
                      className="btn btn--tg-secondary btn--sm"
                      onClick={() => setSelectedPoId("")}
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="card card--gov card--elevated">
          <h3 className="form-section-title" style={{ marginTop: 0 }}>
            Inward Register
          </h3>
          {registerLoading ? (
            <p className="text-muted">Loading register…</p>
          ) : registerError ? (
            <p className="text-muted" style={{ color: "var(--dash-danger, #b91c1c)" }}>
              {registerError}
            </p>
          ) : registerList.length === 0 ? (
            <p className="text-muted">No inward transactions found.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Receipt #</th>
                    <th>PO #</th>
                    <th>Store / Yard</th>
                    <th>Item</th>
                    <th style={{ textAlign: "right" }}>Received Qty</th>
                    <th style={{ textAlign: "right" }}>Damaged Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {registerList.flatMap((r) => {
                    const d = new Date(r.createdAt);
                    const dateStr = Number.isNaN(d.getTime()) ? r.createdAt : d.toLocaleString();
                    return r.lineReceipts.map((l, idx) => (
                      <tr key={`${r.id}-${idx}`}>
                        <td>{dateStr}</td>
                        <td>
                          <strong>{r.id}</strong>
                        </td>
                        <td>{r.poNumber}</td>
                        <td>{r.storeYardName || r.storeYardId}</td>
                        <td>{l.resourceItemLabel}</td>
                        <td style={{ textAlign: "right" }}>
                          {l.qtyReceived} {l.resourceItemUnit}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {l.qtyRejected} {l.resourceItemUnit}
                        </td>
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
